import { FC } from 'react';
import { groupBy } from 'lodash';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import Loader from 'components/new/Loader';
import Account from './Account';
import { type Section } from './types';
import getColumns from './getColumns';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';

const TotalText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  paddingTop: theme.spacing(theme.spacingSizes.xxs),
  paddingBottom: theme.spacing(theme.spacingSizes.xxs),
}));

type Props = {
  sectionTitle: string;
  section?: Section;
  isLoading: boolean;
  baseCurrency: CurrencyCode;
};

const SectionContent: FC<Props> = ({
  sectionTitle,
  section = {
    transactions: [],
    total: 0,
  },
  isLoading,
  baseCurrency,
}) => {
  const columns = getColumns({ baseCurrency });
  const transactionsByAccountId = groupBy(section.transactions, 'accountId');
  const accounts = Object.keys(transactionsByAccountId);
  const hasAccounts = accounts.length > 0;

  return (
    <AccountingSection
      title={sectionTitle}
      noBottomBorder={!isLoading && hasAccounts}
      total={
        isLoading
          ? '--'
          : numberToCurrencyString(section.total, {
              currency: baseCurrency,
              negativeInBrackets: true,
              fallback: 'N/A',
              showFallbackForZero: !hasAccounts,
            })
      }
      totalVariant={!isLoading && hasAccounts ? 'h6' : 'body2'}
      noTransition={isLoading}
      customRow={
        <AccountingTable
          columns={columns}
          rows={[]}
          onRowExpand={() => {}} // Simply to display actions column to match the account table width
          hideHeader
          topBorderOnly
          displayFooterIfEmptyTable
          customFooterRow={[
            {
              key: 'total',
              content: <TotalText variant="h6">{sectionTitle} total</TotalText>,
              colspan: 5,
              backgroundColor: grey[100],
            },
            {
              key: 'totalAmount',
              content: (
                <TotalText variant={isLoading ? 'body2' : 'h6'}>
                  {isLoading
                    ? '--'
                    : numberToCurrencyString(section.total, {
                        currency: baseCurrency,
                        negativeInBrackets: true,
                        fallback: 'N/A',
                        showFallbackForZero: section.transactions.length === 0,
                      })}
                </TotalText>
              ),
              colspan: 6,
              backgroundColor: grey[100],
            },
          ]}
        />
      }
    >
      {isLoading ? (
        <Loader text="Loading new data..." />
      ) : hasAccounts ? (
        accounts.map((account) => (
          <Account
            key={account}
            accountLabel={transactionsByAccountId[account][0].accountLabel}
            transactions={transactionsByAccountId[account]}
            baseCurrency={baseCurrency}
          />
        ))
      ) : (
        <AccountingTable
          columns={columns}
          rows={[]}
          emptyMessage="You have no transactions."
          bodyRowPaddingY={30}
          borderless
          noPaddingX
        />
      )}
    </AccountingSection>
  );
};

export default SectionContent;
